import {
  ApiError,
  Badge,
  Button,
  SparkIconTheme,
  CondensedLabel,
  CondensedSmall,
  FileIntake,
  Flashy,
  Gutter,
  IconThemeProvider,
  Layout,
  LayoutItem,
  Loader,
  NotFoundError,
  ResizableColumns,
  LayoutAnimation,
  ResizableColumnsItem,
  ScrollRegion,
  Divider,
  Suspense,
  Tooltip,
  useApiWithErrors,
} from '@axiom/ui';
import React, { useEffect, useState } from 'react';
import type { ResizableColumnsWidth } from '@axiom/ui';

import { DEFAULT_MATCH_GRID_COLUMNS, MatchGrid } from '../MatchGrid/MatchGrid';
import { ChatAttachmentAcceptUtil } from '../../utils/chat-attachment-accept-util';
import { MatchGridColumnConfigUtil } from '../../utils/match-grid-column-config-util';
import { ChatForm } from '../ChatForm/ChatForm';
import { ChatHistory } from '../ChatHistory/ChatHistory';
import { SparkCandidateDrawerButtons } from '../SparkCandidateDrawerButtons/SparkCandidateDrawerButtons';
import { NewCandidatesBadge } from '../NewCandidatesBadge/NewCandidatesBadge';
import { ChatApi } from '../../api/chat-api';
import { BarredLocationApi } from '../../../api/barred-location-api';
import { UserApi } from '../../../api/user-api';
import { ChatElement } from '../../enums/chat-element';
import { ChatMetric } from '../../enums/chat-metric';
import { DrawerView } from '../../enums/drawer-view';
import { Header } from '../../../components/Header/Header';
import CandidateIcon from '../../../public/svg/avatar-with-mag-glass.svg';
import { useSparkSession } from '../../hooks/useSparkSession';
import { useRecentEvent } from '../../hooks/useRecentEvent';

import { useSparkStatus } from './SparkStatusProvider';

const columnAnimation = LayoutAnimation.for({
  name: ChatElement.COLUMN_GROUP,
});

const HISTORY_WIDTH = '304px';

// `0px`, not `0`: tracks only animate between like units.
const HISTORY_COLLAPSED = '0px';

const DRAWER_TOGGLES_WIDTH = '72px';

export type SparkProps = {
  name: string;
};

export const Spark = ({ name }: SparkProps) => {
  const [sessionUser, columnConfig, barredLocations] = useApiWithErrors(
    UserApi.readSessionUser(),
    ChatApi.readColumnConfig(),
    BarredLocationApi.readBarredLocations()
  );

  if (sessionUser instanceof ApiError) throw sessionUser;
  if (barredLocations instanceof ApiError) throw barredLocations;

  const user = sessionUser.data;

  /*
    **A 404 here is an answer, not a failure**: this reader has never saved a configuration, so the
    grid starts from its defaults. Every other failure is rethrown rather than read the same way -
    "never saved" is what decides the save is a create, and guessing it would mint a second row.
  */
  if (
    columnConfig instanceof ApiError &&
    !(columnConfig instanceof NotFoundError)
  ) {
    throw columnConfig;
  }

  const hasSavedColumns = !(columnConfig instanceof ApiError);
  /*
    Brought up to date here rather than deeper down, so what the grid is handed already names every
    column there is - a column that shipped after this reader last saved is named in neither list.
  */
  const savedColumns = hasSavedColumns
    ? MatchGridColumnConfigUtil.withNewColumns({
        columns: DEFAULT_MATCH_GRID_COLUMNS,
        saved: columnConfig.data,
      })
    : null;

  /*
    Dropped on the way out, because a write never invalidates a read: without this a reader who
    changes their columns and comes back is served the list as it was before the change.
  */
  useEffect(
    () => () => {
      ChatApi.forgetColumnConfig();
    },
    []
  );

  const { setNotice } = useSparkStatus();
  const { selectChat, session, startNewChat } = useSparkSession();
  const {
    scrollRegionRef,
    shelf: attachments,
    transcript: conversation,
  } = session;

  const showSideDrawer = conversation.drawerView !== DrawerView.CLOSED;

  const [seenArrivals, setSeenArrivals] = useState(0);

  useEffect(() => {
    if (!showSideDrawer) return;

    setSeenArrivals(conversation.arrivalCount);
  }, [showSideDrawer, conversation.arrivalCount]);

  const hasUnseenCandidates =
    conversation.matchingCandidates.length > 0 &&
    conversation.arrivalCount !== seenArrivals &&
    !showSideDrawer;

  const candidateCount = conversation.matchingCandidates.length;

  const hasNewCandidates = useRecentEvent({
    eventCount: conversation.arrivalCount,
  });

  /*
    **`grid-template-columns` for the run**: a `px` string is a fixed track, a number a share of
    what is left after them - emitted as `fr`, which normalises against the row's sum. The shares
    are written out of 100 so they read as a percentage of that remainder.
  */
  const columnWidths: Record<DrawerView, ResizableColumnsWidth[]> = {
    [DrawerView.CLOSED]: hasUnseenCandidates
      ? [HISTORY_WIDTH, 92, DRAWER_TOGGLES_WIDTH, 8]
      : [HISTORY_WIDTH, 100, DRAWER_TOGGLES_WIDTH, 0],
    /*
      **44 is the share at which the conversation stops being squeezed**: `Chat` caps its content
      at 768px inside 16px of padding a side plus ~10px of scrollbar, so the track must reach
      ~810px, which 44 does on a 1920 screen. A `px` track would hold that at every size, but
      tracks only animate between like units and the other states are shares.
    */
    [DrawerView.PARTIAL]: [HISTORY_COLLAPSED, 44, DRAWER_TOGGLES_WIDTH, 56],
    [DrawerView.FULL]: [HISTORY_COLLAPSED, 0, DRAWER_TOGGLES_WIDTH, 100],
  };

  return (
    /*
      **The shell is a flex column, so the columns below take what the header leaves.** No
      measurement decides this height - `.spark-body` is the flex child that grows.
    */
    <Layout direction="vertical" height="100dvh" name={name}>
      <LayoutItem>
        <Header
          user={user}
          closeSideDrawer={() => {
            conversation.setDrawerView(DrawerView.CLOSED);
          }}
        />
      </LayoutItem>
      {/*
        Both halves of the theme on one node. `spark-theme.less` matches the id and the provider
        swaps the glyphs, which a stylesheet cannot do - so they go on and off together rather than
        leaving Spark's arrows behind on an otherwise plain page.
      */}
      <IconThemeProvider icons={SparkIconTheme}>
        <div className="spark-body" id="axiom-spark">
          <Layout background="panelLight" direction="vertical" stretched>
            <ResizableColumns
              variation="divider"
              animated
              name={ChatElement.COLUMN_GROUP}
              widths={columnWidths[conversation.drawerView]}
              duration="0.5s"
            >
              <ResizableColumnsItem
                background="panelLight"
                name={ChatElement.HISTORY_COLUMN}
              >
                <Layout direction="vertical" stretched>
                  <Gutter horizontal="8px" vertical="16px">
                    <Button
                      alignContent="left"
                      fluid
                      icon="pencil"
                      iconPosition="left"
                      name="NEW_CHAT"
                      onClick={startNewChat}
                      pattern="primary"
                      variation="minimal"
                    >
                      New Chat
                    </Button>
                  </Gutter>

                  <Divider
                    bottom="16px"
                    horizontal="16px"
                    name="NEW_CHAT_SEPARATOR"
                  />

                  <LayoutItem fluid overflow="scroll">
                    <Suspense
                      stretched
                      fallback={
                        <Layout
                          name="CHAT_HISTORY_LOADING"
                          position="center middle"
                          stretched
                        >
                          <Loader
                            name="CHAT_HISTORY_LOADING_DOTS"
                            type="dots"
                          />
                        </Layout>
                      }
                    >
                      <ChatHistory
                        activeChatId={conversation.activeChatId}
                        setActiveChatId={({ chatId, isFull }) => {
                          selectChat({ chatId, isFull });
                        }}
                        setNotice={setNotice}
                      />
                    </Suspense>
                  </LayoutItem>
                </Layout>
              </ResizableColumnsItem>

              {/* The column gives its scrolling up: the region inside it is the scrollport. */}
              <ResizableColumnsItem
                background="panelLight"
                name={ChatElement.TRANSCRIPT_COLUMN}
                overflow="visible"
              >
                <ScrollRegion
                  isPaused={columnAnimation.isAnimating}
                  name={ChatElement.SCROLLER}
                  onSettled={columnAnimation.onSettled}
                  pinOffset={ChatMetric.PIN_OFFSET}
                  ref={scrollRegionRef}
                  sizeContainer
                >
                  <FileIntake
                    accept={ChatAttachmentAcceptUtil.accepts}
                    /*
                      A full conversation takes no more sends, so it takes no more attachments
                      either - by drop or by paste. The attach button is disabled for the same
                      reason, so all three ways in agree.
                    */
                    disabled={conversation.isChatFull}
                    dropMessage={
                      <Layout position="middle" direction="vertical">
                        <CondensedLabel>Drop your files here</CondensedLabel>
                        <CondensedSmall>
                          {ChatAttachmentAcceptUtil.acceptSummary()}
                        </CondensedSmall>
                      </Layout>
                    }
                    name="CHAT_DROP_ZONE"
                    onFiles={attachments.addDroppedFiles}
                  >
                    <ChatForm name="CHAT_CONTAINER" user={user} />
                  </FileIntake>
                </ScrollRegion>
              </ResizableColumnsItem>

              <ResizableColumnsItem
                background="panelGray"
                edgeShadow="left"
                edgeShadowInset={!showSideDrawer}
                name={ChatElement.DRAWER_TOGGLES_COLUMN}
                overflow="visible"
              >
                <Layout direction="vertical" position="middle" stretched>
                  <LayoutItem
                    bottomGutter="16px"
                    name="NEW_CANDIDATES_SLOT"
                    topGutter={hasNewCandidates ? '16px' : undefined}
                  >
                    <NewCandidatesBadge
                      name="HAS_NEW_CANDIDATES"
                      show={hasNewCandidates}
                    />
                  </LayoutItem>
                  <LayoutItem bottomGutter="16px">
                    <Tooltip
                      fade
                      hideClose
                      name="BADGE_EXPLANATION"
                      position="left"
                      variation="minimal"
                      anchor={
                        <Layout direction="vertical" position="middle">
                          <Badge
                            name="CANDIDATE_COUNT"
                            background={
                              candidateCount === 0 ? 'gray' : 'purple'
                            }
                          >
                            {candidateCount}
                          </Badge>
                          <Gutter bottom="16px" />
                          <Flashy
                            name="CANDIDATE_ICON"
                            color={
                              candidateCount === 0
                                ? 'controlDisabled'
                                : undefined
                            }
                          >
                            <CandidateIcon width={24} height={24} />
                          </Flashy>
                        </Layout>
                      }
                    >
                      How many Axiom talent are in your current results. The
                      number updates as results change.
                    </Tooltip>
                  </LayoutItem>
                  <LayoutItem fluid stretched>
                    <Layout direction="vertical" position="center" stretched>
                      <SparkCandidateDrawerButtons
                        name="DRAWER_BUTTON_CONTAINER"
                        arrivalCount={conversation.arrivalCount}
                        disabled={candidateCount === 0}
                        drawerView={conversation.drawerView}
                        hasNewCandidates={hasNewCandidates}
                        hasUnseenCandidates={hasUnseenCandidates}
                        setDrawerView={conversation.setDrawerView}
                      />
                    </Layout>
                  </LayoutItem>
                </Layout>
              </ResizableColumnsItem>

              <ResizableColumnsItem
                background="panelLight"
                name={ChatElement.MATCH_GRID_COLUMN}
              >
                <Layout direction="vertical" stretched>
                  <LayoutItem
                    fluid
                    stretched
                    horizontalGutter="16px"
                    verticalGutter="16px"
                  >
                    <MatchGrid
                      name="MATCH_GRID_SIDE_DRAWER"
                      isPeeking={!showSideDrawer}
                      barredLocations={barredLocations.data}
                      hasSavedColumns={hasSavedColumns}
                      matchingCandidates={conversation.matchingCandidates}
                      savedColumns={savedColumns}
                      showColumnConfig
                    />
                  </LayoutItem>
                </Layout>
              </ResizableColumnsItem>
            </ResizableColumns>
          </Layout>
        </div>
      </IconThemeProvider>
    </Layout>
  );
};
